import { useCallback, useMemo, useState } from "react";
import { useFinalizeCheckout } from "@/customer/cart/hooks";
import type { Cart, FinalizeRequest, FinalizeResponse } from "@/customer/cart/types";
import type { AuthUser } from "@/customer/auth/AuthContext";

const IDEMPOTENCY_STORAGE_KEY = "jdr-checkout-idempotency";

// Server cap (mirrors orders.finalize_idempotency_key column width).
const KEY_MAX_LENGTH = 128;

function generateKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // Fallback for older test environments — date + random is enough for
  // a single browser tab; uniqueness across customers is not required.
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function readPersistedKey(): string | null {
  try {
    const v = sessionStorage.getItem(IDEMPOTENCY_STORAGE_KEY);
    return v && v.length <= KEY_MAX_LENGTH ? v : null;
  } catch {
    return null;
  }
}

function persistKey(value: string): void {
  try {
    sessionStorage.setItem(IDEMPOTENCY_STORAGE_KEY, value);
  } catch {
    // sessionStorage unavailable (private mode, quotas) — okay to skip;
    // the key still lives on the in-memory state for this tab.
  }
}

function clearKey(): void {
  try {
    sessionStorage.removeItem(IDEMPOTENCY_STORAGE_KEY);
  } catch {
    // ignore
  }
}

export interface UseFinalizeOptions {
  cart: Cart;
  user: AuthUser | null;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
}

export interface UseFinalize {
  submit(): Promise<FinalizeResponse>;
  isPending: boolean;
  errorMessage: string | null;
  reset(): void;
}

// Wraps the cart hook's `useFinalizeCheckout` with the body-builder and
// the Idempotency-Key lifecycle. The key is generated lazily, persisted
// to sessionStorage so a page refresh mid-request reuses the same key,
// and cleared after a successful submit so the next checkout uses a
// fresh key.
export function useFinalize(opts: UseFinalizeOptions): UseFinalize {
  const finalize = useFinalizeCheckout();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const body: FinalizeRequest = useMemo(() => {
    const addr = opts.cart.payload.delivery_address;
    const lessee: FinalizeRequest["lessee"] = {
      name: opts.contactName,
      address_line1: addr?.line1 ?? "",
      city: addr?.city ?? "",
      state: addr?.state ?? "",
      postal_code: addr?.postal_code ?? ""
    };
    if (addr?.line2) lessee.address_line2 = addr.line2;
    return {
      lessee,
      contact: {
        name: opts.contactName,
        phone: opts.contactPhone,
        email: opts.contactEmail
      },
      apparent_agent_consent: opts.cart.payload.apparent_agent_consent
    };
  }, [opts.cart, opts.contactName, opts.contactPhone, opts.contactEmail]);

  const submit = useCallback(async (): Promise<FinalizeResponse> => {
    setErrorMessage(null);
    const key = readPersistedKey() ?? generateKey();
    persistKey(key);
    try {
      const res = await finalize.mutateAsync({ idempotencyKey: key, body });
      if (res.ok) {
        clearKey();
        return res;
      }
      setErrorMessage(res.error_message ?? "We couldn't confirm your reservation. Please try again.");
      return res;
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : "Network error — please try again.");
      throw err;
    }
  }, [finalize, body]);

  const reset = useCallback(() => {
    clearKey();
    setErrorMessage(null);
  }, []);

  return { submit, isPending: finalize.isPending, errorMessage, reset };
}
